import React from 'react';
import {
  useNavigate,
  type NavigateOptions,
  type RegisteredRouter,
} from '@tanstack/react-router';
import {
  Button, Stack, Text, useTranslation,
} from '@piwikpro/ui-components';

/**
 * Same inference as TanStack `useNavigate()` / `router.navigate`.
 * `TTo` must stay a free type parameter — if it defaults (e.g. to `'.'`),
 * `to` is not checked against the registered route tree.
 * Do not `Pick` `to`/`params` off `NavigateOptions`: that widens `to` to `string`.
 */
type InternalListLinkProps<
  TRouter extends RegisteredRouter,
  TTo extends string | undefined,
  TFrom extends string,
  TMaskFrom extends string,
  TMaskTo extends string,
> = {
  label: string
} & NavigateOptions<TRouter, TFrom, TTo, TMaskFrom, TMaskTo>;

export function InternalListLink<
  TRouter extends RegisteredRouter,
  TTo extends string | undefined,
  TFrom extends string = string,
  TMaskFrom extends string = TFrom,
  TMaskTo extends string = '',
>(props: InternalListLinkProps<TRouter, TTo, TFrom, TMaskFrom, TMaskTo>) {
  const { label } = props;
  const { t } = useTranslation(['administration']);
  const navigate = useNavigate();

  return (
    <div style={{ minWidth: 0, width: '100%', overflow: 'hidden' }}>
      <Stack spacing="extraNarrow" verticalAlignment="center">
        <Stack.Item fillLight>
          <Text as="p" wrapType="noWrapAndCutText">{label}</Text>
        </Stack.Item>
        <Stack.Item>
          <Button
            appearance="simple"
            icon="internal-link"
            size="small"
            ariaLabel={t('platform.internal-list-link.go-to-details', { name: label })}
            onClick={() => navigate(props)}
          />
        </Stack.Item>
      </Stack>
    </div>
  );
}
